/**
 * 월령 계산 — 명세서 §4 (시간대 Asia/Seoul 고정)
 *
 * 서버·클라이언트 어디서 계산하든 같은 값이 나와야 하므로,
 * 로컬 타임존이 아니라 항상 Asia/Seoul의 달력 날짜를 기준으로 계산한다.
 */
export const APP_TIME_ZONE = 'Asia/Seoul';
const seoulFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});
/** 주어진 시각의 Asia/Seoul 기준 달력 날짜 */
export function toSeoulDate(date = new Date()) {
    const parts = seoulFormatter.formatToParts(date);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    return { year: get('year'), month: get('month'), day: get('day') };
}
/** Asia/Seoul 기준 `YYYY-MM-DD` 문자열 */
export function toSeoulDateString(date = new Date()) {
    const { year, month, day } = toSeoulDate(date);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/**
 * `YYYY-MM-DD` → Prisma `@db.Date` 컬럼에 넣을 Date.
 *
 * **UTC 자정**이어야 한다. Postgres는 `date` 컬럼에 들어온 타임스탬프를 UTC로 잘라내므로,
 * `2026-07-27T00:00:00+09:00`(= UTC 07-26 15:00)을 주면 **07-26으로 저장된다**.
 * 서울 자정으로 만들면 날짜가 하루씩 앞으로 밀리는 조용한 버그가 생긴다.
 *
 * 반대로 `DateTime` 컬럼(실제 시각)은 서울 자정을 뜻하는 `+09:00`이 맞다 — 섞지 않는다.
 */
export function toDateOnly(value) {
    if (value instanceof Date)
        return new Date(`${toSeoulDateString(value)}T00:00:00Z`);
    return new Date(`${value.slice(0, 10)}T00:00:00Z`);
}
/** `YYYY-MM-DD` → CalendarDate */
export function parseCalendarDate(value) {
    if (value instanceof Date)
        return toSeoulDate(value);
    const [y, m, d] = value.slice(0, 10).split('-').map(Number);
    return { year: y, month: m, day: d };
}
function compareDay(a, b) {
    if (a.year !== b.year)
        return a.year - b.year;
    if (a.month !== b.month)
        return a.month - b.month;
    return a.day - b.day;
}
/**
 * 두 달력 날짜 사이의 만 개월 수.
 * "생일이 지나야 개월 수가 오른다"는 한국식 만 나이 셈법을 따른다.
 */
export function monthsBetween(from, to) {
    let months = (to.year - from.year) * 12 + (to.month - from.month);
    if (to.day < from.day)
        months -= 1;
    return months;
}
const MS_PER_DAY = 24 * 60 * 60 * 1000;
function toUtcMs(d) {
    return Date.UTC(d.year, d.month - 1, d.day);
}
export function calcAge(birthDate, at = new Date()) {
    const birth = parseCalendarDate(birthDate);
    const today = toSeoulDate(at);
    const totalMonths = Math.max(0, monthsBetween(birth, today));
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    const days = Math.max(0, Math.round((toUtcMs(today) - toUtcMs(birth)) / MS_PER_DAY));
    const label = totalMonths < 12 ? `${totalMonths}개월` : months === 0 ? `${years}세` : `${years}세 ${months}개월`;
    return { years, months, totalMonths, days, label };
}
/**
 * 발달 구간 판정에 쓰는 월령.
 * 24개월 미만 이른둥이는 출산예정일 기반 교정월령을 적용한다(§4).
 * 24개월 이상이 되면 교정을 해제하고 실제 월령을 사용한다.
 */
export function calcDevelopmentAge(birthDate, options = {}, at = new Date()) {
    const actual = calcAge(birthDate, at);
    if (!options.correctedDueDate || actual.totalMonths >= 24) {
        return { ...actual, corrected: false, chronologicalMonths: actual.totalMonths };
    }
    const due = parseCalendarDate(options.correctedDueDate);
    const today = toSeoulDate(at);
    // 예정일이 아직 오지 않았다면 교정월령은 0개월.
    if (compareDay(due, today) > 0) {
        return {
            years: 0,
            months: 0,
            totalMonths: 0,
            days: actual.days,
            label: '0개월',
            corrected: true,
            chronologicalMonths: actual.totalMonths,
        };
    }
    const corrected = calcAge(options.correctedDueDate, at);
    return {
        ...corrected,
        corrected: true,
        chronologicalMonths: actual.totalMonths,
    };
}
/** 학기(연도) 계산 — 3월 시작 기준. 1–2월은 직전 학년도로 본다. */
export function schoolYear(at = new Date()) {
    const { year, month } = toSeoulDate(at);
    return month < 3 ? year - 1 : year;
}
//# sourceMappingURL=age.js.map